import React, { useEffect } from 'react';
import { useSearchResults, useSearchResultsSelectedFilters, widget, WidgetDataType } from '@sitecore-search/react';
import SearchCard from '../../../../components/search-card/c-search-card';
import SearchPagination from '../pagination/pagination';
import SearchTabs from '../search-tabs/search-tabs';
import { useSearchContext } from '../search-provider/search-context';
import Loader from '../../../../components/loader';

const SearchResult = () => {
    const { dataSource, setCurrentKeyword, currentKeyword, setFilterItems, setTotalSearchResults, setCurrentPage, setTotalPages, searchItemsPerPage, autoCorrectSuggestion } = useSearchContext();

    // Function to update URL parameters
    const updateUrlParams = params => {
        if (typeof window === 'undefined') return;

        const url = new URL(window.location);
        const searchParams = new URLSearchParams(url.search);

        // Update or remove parameters based on the provided params object
        Object.entries(params).forEach(([key, value]) => {
            if (value && value !== '') {
                searchParams.set(key, value);
            } else {
                searchParams.delete(key);
            }
        });

        // Update the URL without refreshing the page
        const newUrl = `${url.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
        window.history.replaceState({}, '', newUrl);
    };

    const getDefaultPage = () => {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const page = urlParams.get('page');
            return page ? parseInt(page, 10) : 1;
        }
        return 1;
    };

    const getDefaultSelectedFacets = () => {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const filterType = urlParams.get('filter_type');

            if (filterType) {
                const decodedFilterType = decodeURIComponent(filterType);
                return [
                    {
                        facetId: 'type',
                        facetValueText: decodedFilterType
                    }
                ];
            }
        }
        return [];
    };

    const selectedFilter = useSearchResultsSelectedFilters();

    const defaultSortType = 'relevance';
    const defaultPage = getDefaultPage();
    const defaultSelectedFacets = getDefaultSelectedFacets();

    const {
        widgetRef,
        actions: { onKeyphraseChange },
        state: { page, itemsPerPage, keyphrase },
        queryResult: {
            isLoading,
            isFetching,
            data: {
                total_item: totalItems = 0,
                facet: facets = [],
                content: articles = []
            } = {}
        }
    } = useSearchResults({
        query: query => {
            query.getRequest().setSources([dataSource]);
        },
        state: {
            sortType: defaultSortType,
            page: defaultPage,
            itemsPerPage: searchItemsPerPage,
            keyphrase: currentKeyword,
            selectedFacets: defaultSelectedFacets
        }
    });

    // Trigger search when currentKeyword changes
    useEffect(() => {
        onKeyphraseChange({ keyphrase: currentKeyword });
    }, [currentKeyword, keyphrase, onKeyphraseChange]);

    useEffect(() => {
        if (facets.length > 0) {
            const filterType = facets.find(facet => facet.name === 'type');
            if (filterType) {
                const filterItems = filterType.value.map(value => {
                    return {
                        label: value.text,
                        value: value.text.toLowerCase(),
                        id: value.id
                    };
                });
                setFilterItems(filterItems);
            }
        }
    }, [facets, setFilterItems]);

    // Watch for changes in selectedFilter, page, and currentKeyword to update URL
    useEffect(() => {
        const params = {};

        // Add search query parameter
        if (currentKeyword) {
            params.q = currentKeyword;
        } else {
            params.q = '';
        }

        if (page) {
            params.page = page;
        }
        // update current page in context
        setCurrentPage(page || 1);

        // Add filter parameters
        if (selectedFilter && selectedFilter.length > 0) {
            selectedFilter.forEach(filter => {
                if (filter.facetId && filter.facetValueText) {
                    params[`filter_${filter.facetId}`] = encodeURIComponent(filter.facetValueText);
                }
            });
        } else {
            // Remove filter parameters when no filters are selected
            params.filter_type = '';
        }

        updateUrlParams(params);
    }, [selectedFilter, page, currentKeyword]);

    useEffect(() => {
        setTotalSearchResults(totalItems);
        setCurrentPage(1);
        setTotalPages(Math.ceil(totalItems / itemsPerPage));
    }, [totalItems]);

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    if (isLoading || isFetching) {
        return (
            <Loader />
        );
    }

    if (currentKeyword && !isLoading && !isFetching && totalItems === 0) {
        return (
            <div className="form-alert error">
                {autoCorrectSuggestion ? (
                    <>
                        No results found for &quot;{currentKeyword}&quot;. Did you mean
                        <button
                            type="button"
                            onClick={ event => {
                                event.preventDefault();
                                setCurrentKeyword(autoCorrectSuggestion);
                            }}
                        >
                            <i>&quot;<strong>{autoCorrectSuggestion}</strong>&quot;</i>
                        </button> ?
                    </>
                ) : (
                    <>No results found for &quot;{currentKeyword}&quot;.</>
                )}
            </div>
        );
    }

    return (
        <section ref={widgetRef} className="search-result-block">
            <SearchTabs activeTab={null} />
            <div className="search-result-container" id="search-results-panel">
                {articles && (
                    <div className="search-results-items">
                        {articles.map((item, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={`search-result-item-${index}`} className="search-result-item">
                                <SearchCard
                                    sourceId={dataSource}
                                    itemIndex={index}
                                    itemId={item.id}
                                    cardTitle={item.title}
                                    cardDescription={item.description}
                                    cardLink={item.url}
                                    cardImageUrl={item.image_url}
                                />
                            </div>
                        ))}
                    </div>
                )}
                <SearchPagination
                    currentPage={page}
                    totalPages={totalPages}
                />
            </div>
        </section>
    );
};

const SearchResultWidget = widget(SearchResult, WidgetDataType.SEARCH_RESULTS, 'content');

export default SearchResultWidget;
